/**
 * English profanity filter for a kid platform.
 *
 * Design goals:
 *  - Catch common evasion: leetspeak (sh1t, @ss), and separators (f.u.c.k, s h i t).
 *  - Avoid the "Scunthorpe problem" on a coding platform where words like
 *    "class", "password", "assignment", and "analysis" are everyday vocabulary.
 *
 * Strategy: every banned word matches when it appears as a whole normalized
 * token. A stricter subset (SEVERE_WORDS) additionally matches inside a fully
 * collapsed string, which is how separator- and stretch-based evasion is caught,
 * while ordinary words that merely *contain* a short banned substring stay clean.
 */
/** Leetspeak and symbol substitutions applied before matching. */
const LEET_MAP = {
    '0': 'o',
    '1': 'i',
    '3': 'e',
    '4': 'a',
    '5': 's',
    '6': 'g',
    '7': 't',
    '8': 'b',
    '9': 'g',
    '@': 'a',
    $: 's',
    '!': 'i',
    '+': 't',
    '|': 'l',
};
/**
 * Normalize text for matching: lowercase, apply leetspeak, then strip anything
 * that is not a letter (this removes spaces, dots, dashes, and other separators).
 */
export function normalize(text) {
    let out = '';
    for (const ch of text.toLowerCase()) {
        const mapped = LEET_MAP[ch] ?? ch;
        if (mapped >= 'a' && mapped <= 'z')
            out += mapped;
    }
    return out;
}
/** Split into normalized word tokens (leetspeak applied, separators are boundaries). */
function tokenize(text) {
    return text
        .toLowerCase()
        .split(/[^a-z0-9@$!+|]+/)
        .map((tok) => normalize(tok))
        .filter((tok) => tok.length > 0);
}
/** Collapse runs of the same letter to a single one (fuuuck -> fuck). */
function collapseRuns(s) {
    return s.replace(/(.)\1+/g, '$1');
}
/**
 * The full banned wordlist. Whole-token matches only, unless also listed in
 * SEVERE_WORDS below. Kept broad for a kid audience.
 */
export const PROFANITY_WORDS = [
    // general profanity
    'anus',
    'arse',
    'arsehole',
    'ass',
    'asshole',
    'asshat',
    'asswipe',
    'bastard',
    'bitch',
    'bitches',
    'bollocks',
    'boner',
    'boob',
    'boobs',
    'bugger',
    'bullshit',
    'butthole',
    'buttplug',
    'clit',
    'cock',
    'cocks',
    'cocksucker',
    'coon',
    'crap',
    'cum',
    'cunt',
    'damn',
    'dick',
    'dickhead',
    'dildo',
    'dipshit',
    'douche',
    'douchebag',
    'dumbass',
    'ejaculate',
    'erection',
    'fag',
    'faggot',
    'fap',
    'fart',
    'fuck',
    'fucked',
    'fucker',
    'fuckface',
    'fucking',
    'fuckoff',
    'goddamn',
    'handjob',
    'hell',
    'hoe',
    'horny',
    'jackass',
    'jerkoff',
    'jism',
    'jizz',
    'knob',
    'knobhead',
    'labia',
    'masturbate',
    'masturbation',
    'milf',
    'motherfucker',
    'nigga',
    'nigger',
    'nutsack',
    'orgasm',
    'orgy',
    'penis',
    'piss',
    'pissed',
    'porn',
    'porno',
    'prick',
    'pube',
    'pubes',
    'pussy',
    'queef',
    'rape',
    'rapist',
    'retard',
    'retarded',
    'scrotum',
    'semen',
    'sex',
    'sexy',
    'shag',
    'shit',
    'shithead',
    'shitty',
    'skank',
    'slut',
    'smegma',
    'spunk',
    'testicle',
    'testicles',
    'tit',
    'tits',
    'titty',
    'turd',
    'twat',
    'vagina',
    'wank',
    'wanker',
    'whore',
    // slurs (severe)
    'chink',
    'dyke',
    'gook',
    'kike',
    'nazi',
    'paki',
    'spic',
    'tranny',
];
/**
 * The subset of banned words that are also matched as a substring inside a
 * fully collapsed string. These essentially never appear inside innocent
 * English or programming words, so substring matching is safe for them and
 * catches separator- and stretch-based evasion.
 */
export const SEVERE_WORDS = [
    'fuck',
    'shit',
    'bitch',
    'cunt',
    'asshole',
    'motherfucker',
    'bullshit',
    'dickhead',
    'cocksucker',
    'faggot',
    'nigger',
    'nigga',
    'chink',
    'gook',
    'kike',
    'spic',
    'tranny',
    'retard',
    'whore',
    'pussy',
    'dildo',
];
const PROFANITY_SET = new Set(PROFANITY_WORDS);
/**
 * Return the banned word that caused text to be considered dirty, or null when
 * the text is clean.
 */
export function filterReason(text) {
    // Pass 1: whole-token exact match against the full list.
    for (const tok of tokenize(text)) {
        if (PROFANITY_SET.has(tok))
            return tok;
    }
    // Pass 2: severe words as a substring of the fully collapsed string. This
    // catches "f u c k", "s.h.i.t", "fuuuck", "sh1t", "youarefuckingsilly", etc.
    const collapsed = collapseRuns(normalize(text));
    for (const word of SEVERE_WORDS) {
        if (collapsed.includes(word))
            return word;
    }
    return null;
}
/** True when the text contains no banned words. */
export function isClean(text) {
    return filterReason(text) === null;
}
//# sourceMappingURL=profanity.js.map